/**
 * Pure derivation helpers for the plan content block (contract doc 31 §10) — no Angular
 * dependencies, no side effects. Every mapping mirrors the backend contract verbatim; the UI
 * never invents state names.
 */

import type { BlockStatus } from "./block.types";
import type { TaskExecution } from "./engine-run-state.types";
import type { PlanData, PlanState } from "./plan.types";

/**
 * UI-facing plan lifecycle state (doc 31 §10.1). Identical to the raw `PlanState` except that
 * the backend's draft `null` is surfaced as the explicit `"draft"` member.
 */
export type PlanUiState = "draft" | "pending_feedback" | "pending_approval" | "approved" | "executed" | "failed" | "rejected";

/**
 * Derives the UI lifecycle state from the raw `PlanState` (doc 31 §10.1): the backend draft
 * state `null` maps to `"draft"` (rendered as "planning…" while the plan is being built);
 * every other state passes through verbatim.
 */
export function derivePlanUiState(state: PlanState): PlanUiState {
  return state ?? "draft";
}

/**
 * Mirrors the backend envelope status mapping (doc 31 §4.2, `ContentBlockFactory.plan`):
 * `"failed"`/`"rejected"` → `"error"`, `"executed"` → `"completed"`, everything else
 * (including the draft `null`) → `"pending"`.
 *
 * The block's `value.status` is only this coarse 3-value simplification — the fine lifecycle
 * detail ALWAYS derives from `data.state`, never from `value.status`.
 */
export function mapEnvelopeStatus(state: PlanState): BlockStatus {
  if (state === "failed" || state === "rejected") return "error";
  if (state === "executed") return "completed";
  return "pending";
}

/**
 * Confidence chip color for a [0..1] confidence value — green ≥ 0.7, amber ≥ 0.5, red below
 * 0.5 (user ruling 2026-10-11: 0.7 good / 0.5 medium / below bad; replaces the doc 31 §10.2
 * suggestion). No contractual thresholds exist in the payload; this is display-only.
 */
export function confidenceColor(confidence: number): "green" | "amber" | "red" {
  if (confidence >= 0.7) return "green";
  if (confidence >= 0.5) return "amber";
  return "red";
}

/**
 * Display text for a [0..1] confidence value (doc 31 §5.3): `null`/`undefined` marks a
 * deterministic check — confidence is null or 1.0 for deterministic checks — and renders as
 * `"det."`; any numeric value renders as a rounded percentage (0.92 → "92%").
 */
export function formatConfidence(confidence: number | null | undefined): string {
  if (confidence === null || confidence === undefined) return "det.";
  return `${Math.round(confidence * 100)}%`;
}

/**
 * `task_id` of every task that did NOT reach `DONE` (doc 31 §10.1) — the list rendered for a
 * `partial` execution outcome.
 */
export function nonDoneTaskIds(tasks: TaskExecution[]): string[] {
  return tasks.filter((task) => task.state !== "DONE").map((task) => task.task_id);
}

/**
 * True when the engine run is complete: the backend considers the run closed when every task
 * is terminal (`DONE`/`FAILED`) or parked (`WAITING_USER`/`WAITING_INPUT`/`PENDING`, doc 31
 * §10.4). `READY` means still dispatchable, so the run is NOT complete. An empty task list
 * means the run has not been populated yet → NOT complete.
 */
export function isRunComplete(tasks: TaskExecution[]): boolean {
  return (
    tasks.length > 0 &&
    tasks.every(
      (task) =>
        task.state === "DONE" ||
        task.state === "FAILED" ||
        task.state === "WAITING_USER" ||
        task.state === "WAITING_INPUT" ||
        task.state === "PENDING",
    )
  );
}

/**
 * True when the plan is the lightweight dry-run child (doc 31 §6): `plan_type === "dry_run"`
 * AND it carries a non-null `parent_plan_id`. Dry-run children and their parents arrive as
 * independent blocks with distinct `plan_ref`s — never deduplicated against each other.
 */
export function isDryRunChild(plan: PlanData): boolean {
  return plan.plan_type === "dry_run" && plan.parent_plan_id !== null;
}
